
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {join, map, sortBy} from 'lodash';

import Loading from 'components/Loading';
import BooleanSelectFilter, {booleanFilter} from 'components/Table/Filter/Boolean';
import TextFilter from 'components/Table/Filter/Text';
import SimpleTable from 'components/Table/Simple';
import {flexColumnStyle} from 'components/Table/columnHelpers';
import {sortBooleans} from 'components/Table/comparators';
import KyError from 'components/errors/KyError';
import Link from 'components/links/Link';
import ReadableWidthTemplate from 'components/templates/ReadableWidth';
import useAppContext from 'conf/AppContext';
import Types from 'conf/types';
import {useGet} from 'helpers/KyHooks';


export default function CpicPairsPage({}) {
  const {response, error} = useGet('cpic/pair', {parseJson: true});
  const appContext = useAppContext();

  const guidelineExportValue = ({original}) => {
    if (original.clinpgxid) {
      return `${appContext.baseUrl}/guideline/${original.clinpgxid}`;
    }
    return '';
  };

  const columns = [
    {
      id: 'drugname',
      Header: 'Drug',
      accessor: 'drugname',
      sortable: true,
      className: 'text-wrap',
      Cell: DrugCell,
      Filter: TextFilter,
      ...flexColumnStyle({width: 120, grow: 1}),
    },
    {
      id: 'genesymbol',
      Header: 'Gene',
      accessor: 'genesymbol',
      sortable: true,
      Cell: GeneCell,
      Filter: TextFilter,
      ...flexColumnStyle({width: 100}),
    },
    {
      id: 'clinpgxid',
      Header: 'Guideline',
      accessor: 'clinpgxid',
      className: 'text-center',
      Cell: GuidelineCell,
      ...flexColumnStyle({width: 80, shrink: 1}),
      getCellExportValue: guidelineExportValue,
    },
    {
      id: 'cpiclevel',
      Header: 'CPIC Level',
      accessor: 'cpiclevel',
      sortable: true,
      Filter: TextFilter,
      ...flexColumnStyle({width: 60}),
    },
    {
      id: 'provisional',
      Header: 'CPIC Level Status',
      accessor: 'provisional',
      sortable: true,
      sortType: sortBooleans,
      Cell: LevelStatusCell,
      Filter: LevelStatusFilter,
      filter: booleanFilter,
      ...flexColumnStyle({width: 100}),
      getCellExportValue: ({original}) => LevelStatusCell({value: original.provisional}),
    },
    {
      id: 'pgkbcalevel',
      Header: 'ClinPGx Level of Evidence',
      accessor: 'pgkbcalevel',
      sortable: true,
      Filter: TextFilter,
      ...flexColumnStyle({width: 100}),
    },
    {
      id: 'pgxtesting',
      Header: 'PGx on FDA Label',
      accessor: 'pgxtesting',
      sortable: true,
      Filter: TextFilter,
      ...flexColumnStyle({width: 100}),
    },
    {
      id: 'pmids',
      Header: 'CPIC Publications (PMID)',
      accessor: 'pmids',
      sortable: false,
      Cell: PmidsCell,
      ...flexColumnStyle({width: 90}),
      getCellExportValue: ({original}) => join(original.pmids),
    },
  ];
  
  let body = <Loading />;
  if (error) {
    body = <KyError error={error} />;
  } else if (response) {
    const {data: pairs} = response;

    body = (
      <div>
        <SimpleTable
          data={sortBy(pairs, 'cpiclevel', 'drugname', 'genesymbol')}
          columns={columns}
          disableExport={false}
          renderTotalFn={(n) => `${n} Pairs`}
          getExportFileName={() => 'cpic-genes-drugs'}
          initialState={{
            sortBy: [
              {id: 'cpiclevel', desc: false},
              {id: 'drugname', desc: false},
              {id: 'genesymbol', desc: false},
            ],
          }}
        />
      </div>
    );
  }
  
  return (
    <ReadableWidthTemplate title="CPIC Genes-Drugs">
      <p>CPIC assigns CPIC levels to genes/drugs with (1) <Link href="/page/clinAnnLevels">ClinPGx Summary Annotation Levels of Evidence</Link> of 1A, 1B, 2A and 2B, or (2) a <Link href="/page/drugLabelLegend">ClinPGx PGx level</Link> for FDA-approved drug labels of “actionable pgx”, “genetic testing recommended”, or “genetic testing required”, or (3) based on nomination to CPIC for consideration.</p>
      <p>The levels (A, B, C, and D) assigned are subject to change and are initially given a “provisional” CPIC level status; only those gene/drug pairs that have been the subject of guidelines have had sufficient in-depth review of evidence to provide definitive CPIC level assignments (“final” CPIC level status).</p>
      <p>Note that only CPIC level A and B gene/drug pairs have sufficient evidence for at least one prescribing action to be <Link href="https://cpicpgx.org/prioritization/#cpicLevels">recommended</Link>. CPIC level C and D gene/drug pairs are not considered to have adequate evidence or actionability to have prescribing recommendations.</p>
      <ul className="list-unstyled">
        <li><a href="/prioritization/#flowchart">View CPIC’s process for assigning CPIC levels</a></li>
        <li><a href="/prioritization/#cpicLevels">View CPIC’s levels for genes/drugs</a></li>
        <li><a href="https://cpicpgx.org/prioritization-of-cpic-guidelines/">View CPIC’s process for prioritizing CPIC guidelines</a></li>
      </ul>
      <p>CPIC invites <Link href="mailto:feedback@clinpgx.org?subject=CPIC%20Genes-Drugs">feedback</Link> on existing and planned gene/drug guidelines.</p>
      {body}
    </ReadableWidthTemplate>
  );
}

function DrugCell({row, value}) {
  return <Link href={Types.chemical.url(row.original.cpgxdrugid)}>{value}</Link>;
}

function GeneCell({row, value}) {
  return <Link href={`/hgnc/${row.original.genesymbol}`}>{value}</Link>;
}

function LevelStatusCell({value}) {
  return value ? 'Provisional' : 'Final';
}

function LevelStatusFilter(props) {
  return <BooleanSelectFilter yesLabel="Provisional" noLabel="Final" {...props} />;
}

function PmidsCell({value}) {
  return (
    <ul className="pl-1">
      {map(value, (pmid) => <li key={pmid}><Link href={`pmid/${pmid}`}>{pmid}</Link></li>)}
    </ul>
  );
}

function GuidelineCell({value}) {
  if (!value) {
    return null;
  }
  return (
    <Link href={`/guideline/${value}`} title={`Guideline ${value}`}>
      <FontAwesomeIcon size="lg" icon="fa-regular fa-file-lines" />
    </Link>
  );
}
